import React, { useState } from 'react';
import { useSchool as useOps } from '../context/SchoolContext';
import { User, Mail, Phone, Plus, Trash2, X, ShieldCheck, Search, Filter, MapPin, Edit2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import './StaffManager.css';

const StaffManager = ({ isInstructorMode = false }) => {
    const {
        staff = [], instructors = [], branches = [], users = [], selectedBranch,
        addStaff, updateStaff, deleteStaff, addInstructor, updateInstructor, deleteInstructor,
        loading, getBranchName
    } = useOps();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form, setForm] = useState({
        name: '', role: isInstructorMode ? 'Instructor' : 'Staff',
        email: '', phone: '', branch_id: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [editingMember, setEditingMember] = useState(null);

    const getDisplayName = (m) => {
        if (m.name && m.name.trim() !== '' && m.name !== 'Unknown Staff') return m.name;
        if (m.first_name || m.last_name) return `${m.first_name || ''} ${m.last_name || ''}`.trim();
        return `Staff EMP-${m.id}`;
    };

    const data = isInstructorMode ? instructors : staff;
    const addFn = isInstructorMode ? addInstructor : addStaff;
    const updateFn = isInstructorMode ? updateInstructor : updateStaff;
    const deleteFn = isInstructorMode ? deleteInstructor : deleteStaff;

    const filteredStaff = (data || []).filter(s => {
        const displayName = getDisplayName(s);
        const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.role || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesBranch = selectedBranch === 'All Branches' ? true : (s.branchName === selectedBranch || s.branch === selectedBranch);
        return matchesSearch && matchesBranch;
    });

    const handleUserChange = (e) => {
        const selectedId = e.target.value;
        const selectedUser = users.find(u => String(u.id) === String(selectedId));
        setForm(prev => ({
            ...prev,
            user_id: selectedId,
            name: prev.name || (selectedUser ? selectedUser.display_name : ''),
            email: prev.email || (selectedUser ? selectedUser.user_email : '')
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const names = form.name.trim().split(' ');
        const firstName = names[0];
        const lastName = names.slice(1).join(' ') || '';

        const selectedUser = users.find(u => String(u.id) === String(form.user_id));
        const payload = {
            ...form,
            first_name: firstName,
            last_name: lastName,
            user_login: selectedUser ? selectedUser.user_login : '',
            assigned_role: form.role, // Map role to DB schema
            email_address: form.email,
            phone_number: form.phone
        };

        if (editingMember) {
            await updateFn(editingMember.id, payload);
        } else {
            await addFn(payload);
        }

        handleCloseModal();
    };

    const handleEdit = (member) => {
        setEditingMember(member);
        const realName = member.name === 'Unknown Staff' ? '' : (member.name || '');
        setForm({
            name: realName,
            role: member.role || (isInstructorMode ? 'Instructor' : 'Staff'),
            email: member.email || '',
            phone: member.phone || '',
            branch_id: member.branchId || '',
            user_id: member.userId || '',
            license__no: member.license__no || '',
            national_id: member.national_id || ''
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingMember(null);
        setForm({
            name: '', role: isInstructorMode ? 'Instructor' : 'Staff',
            email: '', phone: '', branch_id: '', user_id: '',
            license__no: '', national_id: ''
        });
    };

    if (loading) return <LoadingSpinner color="indigo-600" message="Organizing Team... " />;

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">{isInstructorMode ? 'Instructor Roster' : 'Support Personnel'}</h2>
                    <p className="page-subtitle">{isInstructorMode ? 'Manage your team of certified driving instructors' : 'Manage your administrative and operational support team'}</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-primary flex items-center gap-2"
                    style={{ backgroundColor: isInstructorMode ? '#059669' : '#4f46e5' }}
                >
                    <Plus size={20} /> Onboard {isInstructorMode ? 'Instructor' : 'Staff'}
                </button>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name or designation..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-secondary flex items-center gap-2">
                    <Filter size={16} /> Filter
                </button>
            </div>

            <div className="table-container card fade-in">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>{isInstructorMode ? 'Instructor' : 'Staff Member'}</th>
                            <th>{isInstructorMode ? 'Designation' : 'Role'}</th>
                            <th>Home Branch</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStaff.length > 0 ? filteredStaff.map(member => (
                            <tr key={member.id}>
                                <td>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs border border-indigo-100 uppercase">
                                            {getDisplayName(member)[0] || 'S'}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-slate-800 uppercase tracking-tight">{getDisplayName(member)}</div>
                                            <div className="text-[10px] text-slate-400 font-bold uppercase italic">ID: EMP-{member.id}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`status-badge ${(member.role || 'staff').toLowerCase()}`}>
                                        {member.role || 'Staff'}
                                    </span>
                                </td>

                                <td>
                                    <div className="flex items-center gap-2">
                                        <MapPin size={14} className="text-slate-400" />
                                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                            {getBranchName(member.branch_id) || 'Central Ops'}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="flex gap-1">
                                        <button onClick={() => handleEdit(member)} className="icon-btn text-indigo-600" title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => deleteFn(member.id)} className="icon-btn text-red" title="Remove">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className="text-center py-20 text-secondary italic">No personnel found matching your search.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">{editingMember ? 'Update Personnel' : 'Onboard New Personnel'}</h3>
                            <button onClick={handleCloseModal} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="premium-form">
                            <div className="form-group">
                                <label>ASSIGNED SYSTEM USER</label>
                                <select value={form.user_id} onChange={handleUserChange}>
                                    <option value="">No System Access</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.display_name} ({u.user_login})</option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold italic">Required for Dashboard access</p>
                            </div>

                            <div className="form-group">
                                <label>FULL LEGAL NAME</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="Enter full name"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>ASSIGNED ROLE</label>
                                    <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                                        <option value="Instructor">Instructor</option>
                                        <option value="Receptionist">Receptionist</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Staff">Staff</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>HOME STATION</label>
                                    <select value={form.branch_id} required onChange={e => setForm({ ...form, branch_id: e.target.value })}>
                                        <option value="">Select Branch...</option>
                                        {branches.map(b => (
                                            <option key={b.branch_id} value={b.branch_id}>{b.branch_name || b.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>EMAIL ADDRESS</label>
                                    <input
                                        required
                                        type="email"
                                        placeholder="email@sdt.com"
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>MOBILE NUMBER</label>
                                    <input
                                        type="tel"
                                        placeholder="+263..."
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                    />
                                </div>
                            </div>

                            {isInstructorMode && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <label>LICENSE NUMBER</label>
                                        <input
                                            required
                                            placeholder="Driver's License No."
                                            value={form.license__no || ''}
                                            onChange={e => setForm({ ...form, license__no: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>NATIONAL ID</label>
                                        <input
                                            required
                                            placeholder="National ID"
                                            value={form.national_id || ''}
                                            onChange={e => setForm({ ...form, national_id: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 pt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={handleCloseModal}>Discard</button>
                                <button type="submit" className="btn btn-primary flex-1">{editingMember ? 'Save Changes' : 'Complete Hire'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManager;
